/**
 * The app frame: a compact header carrying freshness, and a five-slot tab bar.
 *
 * Freshness is on every screen because CLAUDE.md §10 makes it a MUST, and says
 * why: in a snapshot model, a stale number presented as live is the only
 * genuinely dangerous failure. Under six hours it is a quiet dot and a phrase.
 * Past six hours it becomes a band across the top that is hard to read past.
 */
import { useEffect } from "react";
import type { ReactNode } from "react";

import { Logo } from "./Logo.js";
import { useApp } from "../state.js";
import { ROUTES, navigate } from "../routes.js";
import type { Route, RouteId } from "../routes.js";
import { buildAlerts, deliver, loadThresholds } from "../alerts.js";
import { deriveDormant } from "../derive/insights.js";
import { viewLimits } from "../view.js";
import { deriveOutstanding } from "../derive/outstanding.js";

/* Inline 24px icons — five of them does not justify an icon dependency. */
const ICONS: Record<string, ReactNode> = {
  dashboard: <path d="M4 13h7V4H4v9Zm0 7h7v-5H4v5Zm9 0h7v-9h-7v9Zm0-16v5h7V4h-7Z" />,
  outstanding: <path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20Zm1 15h-2v-2h2v2Zm0-4h-2V7h2v6Z" />,
  ledgers: <path d="M4 4h13a3 3 0 0 1 3 3v13H7a3 3 0 0 1-3-3V4Zm3 3v2h9V7H7Zm0 4v2h9v-2H7Zm0 4v2h6v-2H7Z" />,
  reports: <path d="M5 21V9h4v12H5Zm5 0V3h4v18h-4Zm5 0v-8h4v8h-4Z" />,
  graphs: <path d="M3 20h18v-2H5V3H3v17Zm4-4 4-5 3 3 5-7 1.6 1.2-6.4 8.8-3-3-2.7 3.4L7 16Z" />,
  more: <path d="M5 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4Z" />,
};

/** Which tab a non-tab screen belongs under, so the bar never looks unselected. */
const TAB_OF: Partial<Record<RouteId, RouteId>> = {
  ledger: "ledgers",
  voucher: "more",
  stock: "more",
  daybook: "more",
  sales: "more",
  search: "more",
  settings: "more",
  "balance-sheet": "reports",
  "profit-loss": "reports",
  "trial-balance": "reports",
  "cash-book": "reports",
  ratios: "reports",
};

/**
 * Fire alerts once the books are actually on screen.
 *
 * Deliberately here and not in the provider: alerts describe what the user is
 * looking at, and a notification racing the first render would arrive before
 * there was anything to go and look at.
 */
function useAlerts() {
  const { snapshot, groups, asOf, flags, connection, generatedAt } = useApp();

  useEffect(() => {
    if (!flags.alerts || !snapshot || !groups || !connection) return;

    const report = deriveOutstanding(snapshot, asOf, groups);
    const overdue = report.receivables
      .flatMap((party) => party.bills.filter((bill) => bill.daysOverdue > 0))
      .map((bill) => ({
        ledger: bill.ledger,
        amount: bill.amount,
        daysOverdue: bill.daysOverdue,
      }));

    const staleHours = generatedAt
      ? (Date.now() - Date.parse(generatedAt)) / 3_600_000
      : null;

    // The largest single debt, for the concentration rule.
    const largest = report.receivables.reduce<{ ledger: string; amount: number } | undefined>(
      (worst, party) =>
        !worst || party.total > worst.amount ? { ledger: party.ledger, amount: party.total } : worst,
      undefined,
    );

    void (async () => {
      const thresholds = await loadThresholds();
      const dormantCount = deriveDormant(snapshot, asOf, groups).customers.length;

      await deliver(
        buildAlerts(
          {
            companyId: connection.companyId,
            overdue,
            staleHours,
            totalReceivable: report.totalReceivable,
            dormantCount,
            thresholds,
            ...(largest ? { largestParty: largest } : {}),
          },
          asOf,
        ),
      );
    })();
  }, [snapshot, groups, asOf, flags.alerts, connection, generatedAt]);
}

export function Shell({ route, children }: { route: Route; children: ReactNode }) {
  useAlerts();
  const { fresh, syncing, refresh, syncNow, pulling, offline, message, snapshot } = useApp();
  const current = ROUTES.find((r) => r.id === route.id);
  const activeTab = TAB_OF[route.id] ?? route.id;
  const stale = fresh?.stale ?? false;
  const limits = viewLimits(snapshot);

  return (
    <div className="min-h-dvh bg-canvas text-ink">
      <header className="sticky top-0 z-20 border-b border-line bg-canvas/85 backdrop-blur-lg">
        <div className="mx-auto flex max-w-3xl items-center gap-2 px-4 pt-3 pb-2.5">
          <div className="min-w-0 flex-1">
            <h1 className="flex items-center gap-2 truncate text-[17px] font-semibold leading-tight tracking-tight">
              {/* Small, and beside the screen title rather than replacing it:
                  the title is what tells you where you are. */}
              <Logo size={17} />
              <span className="truncate">{current?.title ?? "Ledgerly Go"}</span>
            </h1>
            <p className="mt-0.5 flex items-center gap-1.5 truncate text-xs text-ink-3">
              <span
                className="h-1.5 w-1.5 shrink-0 rounded-full"
                style={{ background: offline ? "var(--ink-3)" : stale ? "var(--bad)" : "var(--good)" }}
                aria-hidden
              />
              <span className="truncate">
                {snapshot?.company.name ?? "—"}
                {fresh && <> · {fresh.label}</>}
                {offline && <> · offline</>}
              </span>
              {/* Say which view this is, always. Someone looking at a partial
                  set of figures must never believe they are seeing everything. */}
              {limits.label && (
                <span className="shrink-0 rounded-full bg-accent-wash px-2 py-0.5 text-[10px] font-semibold tracking-wide text-accent">
                  {limits.label}
                </span>
              )}
            </p>
          </div>

          <IconButton label="Search" onClick={() => navigate({ id: "search" })}>
            <path d="M10 4a6 6 0 1 0 3.5 10.9l4.3 4.3 1.4-1.4-4.3-4.3A6 6 0 0 0 10 4Zm0 2a4 4 0 1 1 0 8 4 4 0 0 1 0-8Z" />
          </IconButton>
          <IconButton
            label={syncing ? "Syncing" : "Refresh"}
            onClick={() => void refresh()}
            disabled={syncing || pulling !== null}
          >
            <path
              className={syncing ? "origin-center animate-spin" : ""}
              d="M12 5V2L8 6l4 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7Z"
            />
          </IconButton>
          {/* Distinct from Refresh on purpose: Refresh downloads whatever the
              server already has; this wakes the laptop and waits for it. */}
          <button
            type="button"
            onClick={() => void syncNow()}
            disabled={pulling !== null || syncing}
            className="shrink-0 rounded-full bg-accent px-3 py-1.5 text-xs font-semibold text-accent-ink transition-transform active:scale-95 disabled:opacity-60"
          >
            {pulling ? "Syncing…" : "Sync"}
          </button>
        </div>

        {pulling && (
          <div className="flex items-center justify-center gap-2 bg-accent-wash px-4 py-2 text-xs font-medium text-accent">
            <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 animate-spin" fill="currentColor" aria-hidden>
              <path d="M12 5V2L8 6l4 4V7a5 5 0 1 1-5 5H5a7 7 0 1 0 7-7Z" />
            </svg>
            {pulling}
          </div>
        )}

        {stale && !pulling && (
          <div className="bg-bad-wash px-4 py-2 text-center text-xs font-medium text-bad">
            These figures are {fresh?.label} — the laptop may be off. Do not treat them as live.
          </div>
        )}
        {message && !stale && !pulling && (
          <div className="bg-surface-2 px-4 py-2 text-center text-xs text-ink-2">{message}</div>
        )}
      </header>

      <main className="mx-auto max-w-3xl px-4 pt-4 pb-28">{children}</main>

      <nav
        className="fixed inset-x-0 bottom-0 z-20 border-t border-line bg-canvas/95 backdrop-blur-lg"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto flex max-w-3xl">
          {ROUTES.filter((r) => r.nav && !limits.hidden.has(r.id)).map((r) => {
            const on = r.id === activeTab;
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => navigate({ id: r.id })}
                aria-current={on ? "page" : undefined}
                className="flex flex-1 flex-col items-center gap-0.5 py-2.5 transition-colors"
                style={{ color: on ? "var(--accent)" : "var(--ink-3)" }}
              >
                <svg viewBox="0 0 24 24" className="h-[22px] w-[22px]" fill="currentColor" aria-hidden>
                  {ICONS[r.id]}
                </svg>
                <span className="truncate text-[10.5px]" style={{ fontWeight: on ? 650 : 500 }}>
                  {r.short}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full text-ink-2 transition-colors active:bg-surface-2 disabled:opacity-40"
    >
      <svg viewBox="0 0 24 24" className="h-[19px] w-[19px]" fill="currentColor" aria-hidden>
        {children}
      </svg>
    </button>
  );
}
